import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Camera,
  Check,
  MapPin,
  Truck,
  CornerDownLeft,
  ArrowRight,
  ArrowLeft,
  ScanLine,
  Gauge,
  AlertTriangle,
} from "lucide-react";
import type { PortalState } from "@/store/portal-store";

interface Step7Props {
  state: PortalState;
  setPhoto: (slot: string, dataUrl: string) => void;
  onNext: () => void;
}

const REQUIRED_PHOTOS = [
  { key: "front", label: "Front", icon: Truck, required: true },
  { key: "rear", label: "Rear", icon: CornerDownLeft, required: true },
  { key: "left", label: "Left Side", icon: ArrowLeft, required: true },
  { key: "right", label: "Right Side", icon: ArrowRight, required: true },
  { key: "vin", label: "VIN Plate", icon: ScanLine, required: true, helper: "Close-up, must be legible." },
  { key: "odometer", label: "Odometer", icon: Gauge, required: true, helper: "Dashboard reading, must be legible." },
  { key: "damage", label: "Existing Damage", icon: AlertTriangle, required: false, helper: "Close-up of any pre-existing damage." },
];

export function Step7Photos({ state, setPhoto, onNext }: Step7Props) {
  const [gpsActive] = useState(true);
  const [capturing, setCapturing] = useState<string | null>(null);
  const [previewSlot, setPreviewSlot] = useState<string | null>(null);

  const requiredCount = REQUIRED_PHOTOS.filter((p) => p.required).length;
  const capturedRequired = REQUIRED_PHOTOS.filter(
    (p) => p.required && state.photos[p.key]
  ).length;
  const allRequired = capturedRequired === requiredCount;

  const handleCapture = (key: string) => {
    if (!gpsActive) return;
    setCapturing(key);
    // Simulate photo capture
    setTimeout(() => {
      setPhoto(key, `captured_${key}_${Date.now()}`);
      setCapturing(null);
    }, 1000);
  };

  if (capturing) {
    const slot = REQUIRED_PHOTOS.find((p) => p.key === capturing);
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col items-center justify-center">
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => setCapturing(null)}
            className="text-white text-sm"
          >
            Cancel
          </button>
          <span className="text-white/60 text-sm">{slot?.label}</span>
          <div />
        </div>

        <div className="w-72 h-48 border-2 border-white/40 rounded-xl relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-60 h-40 border border-white/20 rounded-lg" />
          </div>
        </div>

        <div className="flex items-center gap-2 text-green-400 text-xs mb-4">
          <MapPin className="w-3 h-3" />
          <span>32.7767, -96.7970</span>
        </div>

        <div className="w-16 h-16 rounded-full border-4 border-white bg-white/20 animate-pulse" />
        <p className="text-white/60 text-sm mt-3">Capturing...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 py-6 pb-24">
      <h2 className="text-xl font-bold mb-2">Take Photos of Your Truck</h2>
      <p className="text-gray-500 text-sm mb-4">
        Take clear photos in good lighting. GPS must be enabled.
      </p>

      {/* GPS Badge */}
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium mb-6 w-fit",
          gpsActive
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
        )}
      >
        <MapPin className="w-3 h-3" />
        {gpsActive ? "Location Active" : "Location Required — Enable GPS"}
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {REQUIRED_PHOTOS.map((photo) => {
          const captured = !!state.photos[photo.key];
          const Icon = photo.icon;

          return (
            <button
              key={photo.key}
              type="button"
              onClick={() =>
                captured ? setPreviewSlot(photo.key) : handleCapture(photo.key)
              }
              className={cn(
                "relative rounded-xl border-2 border-dashed p-4 flex flex-col items-center justify-center gap-2 transition-all min-h-[100px]",
                captured
                  ? "border-green-400 bg-green-50"
                  : "border-gray-200 bg-gray-50 hover:border-gray-300",
                !photo.required && "col-span-2"
              )}
            >
              {captured ? (
                <>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <span className="text-xs text-green-700 font-medium">
                    {photo.label}
                  </span>
                  <span className="text-[10px] text-green-500 flex items-center gap-1">
                    <MapPin className="w-2 h-2" /> 32.7767, -96.7970
                  </span>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gray-400" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    {photo.label}
                    {!photo.required && " (Optional)"}
                  </span>
                  {photo.helper && (
                    <span className="text-[10px] text-gray-400">
                      {photo.helper}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-gray-400 text-center mb-4">
        {capturedRequired}/{requiredCount} required photos captured
      </p>

      {/* Sticky bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 pb-6">
        <Button
          className="w-full h-12 text-base max-w-md mx-auto block"
          disabled={!allRequired}
          onClick={onNext}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
